import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Tooltip,
} from '@material-ui/core';
import { MapboxBasic } from 'components/MapBox/Basic';
import { ZonesOverlayFeatureStateObject } from 'components/MapBox/ZonesOverlay/ZonesOverlay';
import { AreaRating } from 'const/AreaRating';
import { CourierType } from 'const/CourierType';
import { MapZonesType } from 'const/Map/MapZonesType';
import { PartitionType } from 'const/PartitionType';
import { ZoneRatingJson } from 'fetch/fetchRatings';
import { usePartitionsAction } from 'hooks/usePartitionsAction';
import React, { useEffect, useRef, useState } from 'react';
import { CalendarInputComponent } from 'ReusableComponents/CalendarInput/CalendarInputComponent';
import { Region } from 'types/orderTypes';
import { bemComponent } from './bemComponent';
import { PartitionList } from './PartitionList/PartitionList';
import { Section } from './Section/section';

interface PartitionsWrapperProps {
  region: Region;
  activeDate: string;
  today: string;
  couriers: Array<CourierType>;
  partitionList: Array<PartitionType>;
  zoneCount: Record<string, number>;
  zoneRatings: ZoneRatingJson[];
  onDateChanged: (date: string) => void;
  refreshData: () => void;
}

export const PartitionsWrapper = ({
  region,
  activeDate,
  today,
  onDateChanged,
  refreshData,
  ...props
}: PartitionsWrapperProps) => {
  const bem = bemComponent('partitions');

  const partitionedZones = props.partitionList?.flatMap(
    partition => partition.zones
  );
  const unpartitionedOrderCount = Object.keys(props.zoneCount)
    .filter(zoneId => !partitionedZones.includes(zoneId))
    .reduce((acc, zoneId) => acc + props.zoneCount[zoneId], 0);
  const unpartitionedZones =
    Object.keys(props.zoneCount).length - partitionedZones.length;

  const selectedPartitions: ZonesOverlayFeatureStateObject[] =
    props.partitionList
      ?.reduce((acc, partition, partitionIndex) => {
        const zonesOverlayObjects = partition.zones?.map(zone => ({
          id: zone,
          value: {
            partition: (partitionIndex + 1).toString(),
            rating: null,
          },
        }));

        return [...acc, zonesOverlayObjects];
      }, [])
      .flat();

  const partitionsAction = usePartitionsAction(activeDate);

  const lastSelectedPartition = useRef<PartitionType>(null);

  const [activePartition, setActivePartition] = useState<PartitionType>();
  const [activePartitionIndexOnMap, setActivePartitionIndexOnMap] =
    useState<number>();

  const [
    selectedPartitionsWithHoverOnCourier,
    setSelectedPartitionsWithHoverOnCourier,
  ] = useState<ZonesOverlayFeatureStateObject[]>();

  const [showOrderCounts, setShowOrderCounts] = useState(true);

  const onCalendarDateChanged = (date: string) => {
    onDateChanged(date);
    lastSelectedPartition.current = null;
  };

  /* Copy the Partitioning to Today */
  const copyPartitioningToToday = async () => {
    const copied = await partitionsAction.copy(activeDate, today, region);

    if (copied) {
      onCalendarDateChanged(today);
    }
  };

  /* Auto Partition */
  const onAutoPartitionClicked = async () => {
    await partitionsAction.autoPartition(activeDate, region);
    refreshData();
  };

  /* Auto Assign Couriers */
  const onAutoAssignCouriersClicked = async () => {
    await partitionsAction.autoAssignCouriers(activeDate, region);
    refreshData();
  };

  /* Create new partition */
  const createPartition = async () => {
    const added = await partitionsAction.add(activeDate, region);
    if (added) {
      lastSelectedPartition.current = null;
    }
    refreshData();
  };

  /* Adding courier to Partition */
  const addCourierToPartition = async (
    courier: CourierType,
    partition: PartitionType
  ) => {
    await partitionsAction.editCourier(activeDate, courier, partition);
    refreshData();
  };

  /* Remove courier from Partition */
  const removeCourierFromPartition = async (courier: CourierType) => {
    await partitionsAction.editCourier(activeDate, courier);
    refreshData();
  };

  /* Adding courier to ActivePartition */
  const addCourierToActivePartition = async (courier: CourierType) => {
    await partitionsAction.editCourier(activeDate, courier, activePartition);
    refreshData();
  };

  const onHoverCourier = (
    couriers?: CourierType[],
    ratingType?: AreaRating
  ) => {
    if (couriers) {
      /* Hover-in */
      const newSelectedPartitionsWithHoverOnCourier = selectedPartitions.map(
        partition => {
          const zoneId = partition.id;
          const newPartition = { ...partition };

          /* Add the Courier's Preferred Area */
          props.zoneRatings.map(zoneRating => {
            const isSelectedCourier = couriers.some(
              courier =>
                courier.id.toString() === zoneRating.courierId.toString()
            );

            const isMatchedRatingType =
              (ratingType && zoneRating.rating === ratingType) || !ratingType;

            if (
              isSelectedCourier &&
              zoneRating.zoneId === zoneId &&
              isMatchedRatingType
            ) {
              newPartition.value = {
                ...newPartition.value,
                rating: zoneRating.rating,
              };
            }

            return true;
          });

          return newPartition;
        }
      );

      setSelectedPartitionsWithHoverOnCourier(
        newSelectedPartitionsWithHoverOnCourier
      );
    } else {
      /* Hover-out */
      setSelectedPartitionsWithHoverOnCourier(undefined);
    }
  };

  const onClickZonesOverlay = async (
    selectedZone: string | number,
    currentFeatureState: any
  ) => {
    const currentZonePartition: number | undefined =
      currentFeatureState['partition'];
    const partitionIndexForSelectedZone = currentZonePartition - 1;

    /**
     * When the selected zones is empty, `newZonePartition` should be changed to `activePartition's ID`.
     * When the selected zones is not empty :
     * - selected zones IS `activePartition's ID` => Unselect zones
     * - selected zones IS NOT `activePartition's ID` => Change the zones to `activePartition's ID`
     */
    let newZonePartition: PartitionType;
    if (
      !!currentZonePartition &&
      partitionIndexForSelectedZone === activePartitionIndexOnMap
    ) {
      newZonePartition = null;
    } else {
      newZonePartition = activePartition;
    }

    await partitionsAction.editZone(
      activeDate,
      selectedZone.toString(),
      newZonePartition
    );
    refreshData();
  };

  /* Always make the last pastition as the Active Partition by default */
  useEffect(() => {
    const lastPartitionIndex = props.partitionList?.length - 1 || 0;
    const lastPartition = props.partitionList?.[lastPartitionIndex];

    const currentActivePartition =
      lastSelectedPartition.current || lastPartition || null;
    const partitionIndex = lastSelectedPartition.current
      ? props.partitionList?.findIndex(
          data => data.id === currentActivePartition.id
        ) || 0
      : lastPartitionIndex;
    setActivePartition(currentActivePartition);
    setActivePartitionIndexOnMap(partitionIndex);
  }, [props.partitionList]);

  /* Selecting Partition */
  const onSelectPartition = (partition: PartitionType) => {
    setActivePartition(partition);

    const partitionIndex =
      props.partitionList?.findIndex(data => data.id === partition.id) || 0;
    setActivePartitionIndexOnMap(partitionIndex + 1);
    lastSelectedPartition.current = partition;
  };

  return (
    <div className={bem.className}>
      <div className={bem.element('sidebar')}>
        <Section>
          <CalendarInputComponent
            date={activeDate}
            setDate={onCalendarDateChanged}
          />
        </Section>

        <Section>
          <h2>Partitions Overview</h2>
          <p>Unpartitioned orders: {unpartitionedOrderCount}</p>
          <p>Unpartitioned zones: {unpartitionedZones}</p>
        </Section>

        <Section>
          <Grid container spacing={1} className="button-group">
            {activeDate !== today && (
              <Grid item>
                <Button variant="outlined" onClick={copyPartitioningToToday}>
                  Copy partitioning to today
                </Button>
              </Grid>
            )}

            <Grid item>
              <Tooltip title="AutoPartition will check on the previous partitioned Zones and Couriers from the last 100 days">
                <Button variant="outlined" onClick={onAutoPartitionClicked}>
                  Auto partition
                </Button>
              </Tooltip>
            </Grid>
          </Grid>

          <Grid container spacing={1} className="button-group">
            <Grid item>
              <Button variant="outlined" onClick={onAutoAssignCouriersClicked}>
                Auto-assign couriers
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" onClick={createPartition}>
                Create new partition
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={1}>
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showOrderCounts}
                    onChange={(e, checked) => setShowOrderCounts(checked)}
                  />
                }
                label="Show order counts"
              />
            </Grid>
          </Grid>
        </Section>

        {props.partitionList && props.zoneCount && props.couriers && (
          <PartitionList
            data={props.partitionList}
            zonePartitionsCount={props.zoneCount}
            activePartition={activePartition}
            couriers={props.couriers}
            onSelectPartition={onSelectPartition}
            addCourierToPartition={addCourierToPartition}
            removeCourierFromPartition={removeCourierFromPartition}
            addCourierToActivePartition={addCourierToActivePartition}
            onHoverCourier={onHoverCourier}
          />
        )}
      </div>

      <MapboxBasic
        hasZonesOverlay
        zonesOverlayHideLabel={!showOrderCounts}
        region={region}
        zonesOverlayObjects={
          selectedPartitionsWithHoverOnCourier || selectedPartitions
        }
        zonesOverlayType={MapZonesType.Partition}
        zonesOverlayCountData={props.zoneCount}
        onClickZonesOverlay={onClickZonesOverlay}
      />
    </div>
  );
};
